import {
  HideOnMobile,
  HideOnSmallerMobile,
  ShowOnSmallerMobile,
} from 'components/core/layout/Responsive'
import useIsMobile from 'hooks/util/useIsMobile'
import React from 'react'
import styled from 'styled-components'
import { useAccount } from 'wagmi'
import { trpc } from '~/utils/trpc'

const CallToActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;

  height: 84px;
  width: 340px;

  border: 2px solid #080a0c;
  border-radius: 100px;

  padding: 0 16px;

  will-change: transform;
  transform: scale(1) translate3d(0, 0, 0);

  overflow: hidden;

  transition: all 0.2s ease-in-out;
  &:hover {
    cursor: pointer;
    transform: scale(1.02);
  }

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    height: auto; 
    flex-direction: column; 
    justify-content: center;
    align-items: center;
    
    border-radius: 32px; 
    width: auto;

    width: calc(50% - 8px); 
    aspect-ratio: 1/1;
    padding: 4px 6px;
    gap: 10px;
    letter-spacing: -0.03em;

  `}
`

const LeftCell = styled.div`
  border-radius: 26px;

  height: 52px;
  width: 52px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  font-weight: 700;
  font-size: 22px;

  background: #080a0c;

  border: 3px solid #080a0c;

  box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.06), 0px 12px 16px rgba(0, 0, 0, 0.04),
    0px 4px 4px rgba(0, 0, 0, 0.03), inset 0px 4px 4px rgba(255, 255, 255, 0.25);
`

const DoubleTextCell = styled.div`
  display: flex;
  flex-direction: column;

  margin-left: 12px;
  gap: 3px;

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
  margin-left: 0px; 
    text-align: center; 
`}
`

const TopText = styled.div`
  font-weight: 700;
  font-size: 20px;

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    font-size: 18px;
  `}
`

const BottomText = styled.div`
  font-size: 14px;
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    font-weight: 500;
    font-size: 12px;
  `}
`

const RightImage = styled.img`
  position: absolute;
  right: 4px;
  bottom: 0;
  object-position: bottom right;
  height: 92px;
  width: 92px;
  object-fit: contain;
`

export enum CellTheme {
  DARK,
  LIGHT,
}

const cellStyles = {
  [CellTheme.LIGHT]: {
    background: 'radial-gradient(100% 100% at 50% 0%, #94fe93 0%, #7df8d5 85%)',
    topTextColor: 'black',
    bottomTextColor: '#4a4b51',
    boxShadow: '',
  },
  [CellTheme.DARK]: {
    background: '#26262B',
    topTextColor: 'white',
    bottomTextColor: '#B8BCCF',
    boxShadow:
      '0px 1.49px 4.0375px rgba(255, 255, 255, 0.2), inset 0px 8px 12px rgba(0, 0, 0, 0.25)',
  },
}

function Plus() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 0H6V6H0V10H6V16H10V10H16V6H10V0Z" fill="white" />
    </svg>
  )
}
function CallToActionCell({
  leftText,
  topText,
  bottomText,
  onClick,
  cellStyle = CellTheme.LIGHT,
}: {
  leftText?: string
  topText: string
  bottomText: string
  onClick: () => void
  cellStyle?: CellTheme
}) {
  const { background, boxShadow, topTextColor, bottomTextColor } = cellStyles[cellStyle]

  return (
    <CallToActionContainer style={{ background, boxShadow }} onClick={onClick}>
      <LeftCell>{leftText || <Plus />}</LeftCell>
      <DoubleTextCell>
        <TopText style={{ color: topTextColor }}>{topText}</TopText>
        <BottomText style={{ color: bottomTextColor }}>{bottomText}</BottomText>
      </DoubleTextCell>
    </CallToActionContainer>
  )
}

function SummonGobblerCell({
  cellStyle = CellTheme.LIGHT,
  onClick,
  bottomText,
}: {
  cellStyle?: CellTheme
  onClick: () => void
  bottomText: string
}) {
  const { background, boxShadow, topTextColor, bottomTextColor } = cellStyles[cellStyle]

  return (
    <CallToActionContainer style={{ background, boxShadow }} onClick={onClick}>
      <LeftCell>
        <Plus />
      </LeftCell>
      <DoubleTextCell>
        <TopText style={{ color: topTextColor }}>Summon Gobbler</TopText>
        <BottomText style={{ color: bottomTextColor }}>{bottomText}</BottomText>
      </DoubleTextCell>
      <HideOnSmallerMobile>
        <RightImage src="/images/dashboard/flub_summon.png" />
      </HideOnSmallerMobile>
    </CallToActionContainer>
  )
}

function ArtSubmissionsCard({ onClick }: { onClick: () => void }) {
  const { address: walletAddress } = useAccount()
  const { data: submissions } = trpc.useQuery(
    ['submissions.getUnreadSubmissions', { walletAddress: walletAddress! }],
    { enabled: !!walletAddress },
  )

  const cellStyle = (submissions?.length ?? 0) > 0 ? CellTheme.LIGHT : CellTheme.DARK
  const topText = submissions?.length
  const { background, boxShadow, topTextColor, bottomTextColor } = cellStyles[cellStyle]

  const isMobile = useIsMobile()

  if (!isMobile) {
    return <div />
  }

  return (
    <CallToActionContainer style={{ background, boxShadow }} onClick={onClick}>
      <DoubleTextCell>
        <TopText style={{ fontSize: 36, color: topTextColor }}>{topText}</TopText>
        <BottomText style={{ color: bottomTextColor }}>New art submissions</BottomText>
      </DoubleTextCell>
    </CallToActionContainer>
  )
}

export { CallToActionCell, SummonGobblerCell, ArtSubmissionsCard }
